import { useState } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import EventsHero from './components/EventsHero';
import OurEventsSection from './components/OurEventsSection';
import LocalEventsSection from './components/LocalEventsSection';
import EventDetailModal from './components/EventDetailModal';
import RsvpModal from './components/RsvpModal';
import SubmitEventModal from './components/SubmitEventModal';
import AccountGateModal from '../../components/feature/AccountGateModal';
import { allEvents, CalendarEvent } from '../../mocks/eventsCalendar';
import KeepExploring from '../../components/feature/KeepExploring';
import SEO from '../../components/feature/SEO';
import { Link } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';

export default function EventsPage() {
  const { isLoggedIn, user } = useAuth();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedEvent, setSelectedEvent] = useState<CalendarEvent | null>(null);
  const [rsvpEvent, setRsvpEvent] = useState<CalendarEvent | null>(null);
  const [pendingRsvpEvent, setPendingRsvpEvent] = useState<CalendarEvent | null>(null);
  const [showAuthGate, setShowAuthGate] = useState(false);
  const [isGuestView, setIsGuestView] = useState(false);
  const [showSubmitModal, setShowSubmitModal] = useState(false);

  const matchesSearch = (ev: CalendarEvent) => {
    const q = searchQuery.toLowerCase();
    return (
      !q ||
      ev.title.toLowerCase().includes(q) ||
      ev.location.toLowerCase().includes(q) ||
      ev.description.toLowerCase().includes(q)
    );
  };

  const ourEvents = allEvents
    .filter((ev) => ev.category === 'mommy-mingle' && matchesSearch(ev))
    .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

  const localEvents = allEvents
    .filter((ev) => ev.category !== 'mommy-mingle' && matchesSearch(ev))
    .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

  const siteUrl = import.meta.env.VITE_SITE_URL ?? '';

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: 'Local Mom Events in Gwinnett County | The Mommy Mingle',
    description:
      'Family-friendly events, mom meetups, and community activities in Gwinnett County and surrounding areas.',
    url: `${siteUrl}/events`,
    publisher: {
      '@type': 'Organization',
      name: 'The Mommy Mingle',
      url: siteUrl,
    },
  };

  const handleRsvpClick = (event: CalendarEvent) => {
    if (isLoggedIn && user) {
      setSelectedEvent(null);
      setRsvpEvent(event);
    } else {
      setPendingRsvpEvent(event);
      setSelectedEvent(null);
      setShowAuthGate(true);
    }
  };

  const handleModalRsvp = () => {
    if (isLoggedIn && user && selectedEvent) {
      setRsvpEvent(selectedEvent);
      setSelectedEvent(null);
    } else {
      setPendingRsvpEvent(selectedEvent);
      setShowAuthGate(true);
    }
  };

  const handleAuthSuccess = () => {
    setShowAuthGate(false);
    setIsGuestView(false);
    if (pendingRsvpEvent) {
      setRsvpEvent(pendingRsvpEvent);
      setPendingRsvpEvent(null);
    }
  };

  const handleContinueAsGuest = () => {
    setShowAuthGate(false);
    setIsGuestView(true);
    if (pendingRsvpEvent) {
      setSelectedEvent(pendingRsvpEvent);
      setPendingRsvpEvent(null);
    }
  };

  const handleGuestUnlock = () => {
    setPendingRsvpEvent(selectedEvent);
    setShowAuthGate(true);
  };

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Local Mom Events in Gwinnett County | The Mommy Mingle"
        description="Find family-friendly events, mom meetups, and community activities in Gwinnett County and surrounding areas. Submit your event to reach thousands of local moms."
        keywords="mom events Gwinnett County, family events Georgia, mom meetups Sugar Hill, local events for families"
        canonical="/events"
        schema={schema}
      />
      <Navbar />

      <EventsHero
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        onSubmitEvent={() => setShowSubmitModal(true)}
      />

      {/* Member Perks Info Strip */}
      <div className="bg-rose-600 py-4 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="flex flex-wrap items-center gap-4 sm:gap-8">
              <div className="flex items-center gap-2 text-white">
                <div className="w-6 h-6 flex items-center justify-center shrink-0">
                  <i className="ri-vip-crown-fill text-sm"></i>
                </div>
                <span className="text-xs font-semibold whitespace-nowrap">Members get early access to RSVPs</span>
              </div>
              <div className="hidden sm:block w-px h-4 bg-white/30"></div>
              <div className="flex items-center gap-2 text-white">
                <div className="w-6 h-6 flex items-center justify-center shrink-0">
                  <i className="ri-price-tag-3-fill text-sm"></i>
                </div>
                <span className="text-xs font-semibold whitespace-nowrap">Exclusive member pricing on select events</span>
              </div>
              <div className="hidden sm:block w-px h-4 bg-white/30"></div>
              <div className="flex items-center gap-2 text-white">
                <div className="w-6 h-6 flex items-center justify-center shrink-0">
                  <i className="ri-lock-star-fill text-sm"></i>
                </div>
                <span className="text-xs font-semibold whitespace-nowrap">Members-only curated events</span>
              </div>
            </div>
            <Link
              to="/membership"
              className="flex items-center gap-1.5 px-4 py-2 bg-white text-rose-600 text-xs font-bold rounded-full hover:bg-rose-50 transition-colors cursor-pointer whitespace-nowrap shrink-0"
            >
              <i className="ri-vip-crown-line text-sm"></i>
              See Membership Perks
            </Link>
          </div>
        </div>
      </div>

      {searchQuery && (
        <div className="bg-pink-50 border-b border-pink-100 py-3 px-4">
          <div className="max-w-7xl mx-auto flex items-center justify-between gap-4">
            <p className="text-sm text-pink-700 font-medium">
              Showing results for &ldquo;<strong>{searchQuery}</strong>&rdquo; —&nbsp;
              {ourEvents.length + localEvents.length} event{ourEvents.length + localEvents.length !== 1 ? 's' : ''} found
            </p>
            <button
              onClick={() => setSearchQuery('')}
              className="text-xs text-pink-500 hover:text-pink-700 font-semibold cursor-pointer whitespace-nowrap"
            >
              Clear search
            </button>
          </div>
        </div>
      )}

      {ourEvents.length > 0 ? (
        <OurEventsSection
          events={ourEvents}
          onEventClick={setSelectedEvent}
          onRsvpClick={handleRsvpClick}
        />
      ) : searchQuery ? (
        <section id="our-events" className="py-14 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="inline-flex items-center gap-2 bg-pink-100 text-pink-700 text-xs font-bold px-3 py-1 rounded-full mb-3 uppercase tracking-wide">
              <span className="w-2 h-2 rounded-full bg-pink-500 inline-block"></span>
              Hosted by The Mommy Mingle
            </div>
            <h2 className="text-3xl font-bold text-gray-900 mb-3" style={{ fontFamily: "'Playfair Display', serif" }}>
              Our Events
            </h2>
            <p className="text-sm text-gray-400">No Mommy Mingle events match your search.</p>
          </div>
        </section>
      ) : null}

      <div className="h-px bg-gray-100 mx-auto max-w-7xl"></div>

      {localEvents.length > 0 ? (
        <LocalEventsSection
          events={localEvents}
          onEventClick={setSelectedEvent}
          onSubmitEvent={() => setShowSubmitModal(true)}
        />
      ) : searchQuery ? (
        <section id="local-events" className="py-14 bg-gray-50/70">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="inline-flex items-center gap-2 bg-slate-100 text-slate-600 text-xs font-bold px-3 py-1 rounded-full mb-3 uppercase tracking-wide">
              <i className="ri-map-pin-2-line text-slate-500 text-xs"></i>
              Around Gwinnett &amp; Surrounding Areas
            </div>
            <h2 className="text-3xl font-bold text-gray-900 mb-3" style={{ fontFamily: "'Playfair Display', serif" }}>
              Local Events Around Town
            </h2>
            <p className="text-sm text-gray-400">No local events match your search.</p>
          </div>
        </section>
      ) : null}

      {/* Partner callout */}
      <div className="bg-gray-950 py-5 px-4">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 flex items-center justify-center bg-rose-500/20 rounded-full shrink-0">
              <i className="ri-handshake-fill text-rose-400 text-sm"></i>
            </div>
            <p className="text-white text-sm font-semibold">
              Want visibility in front of 3,000+ local moms?
            </p>
          </div>
          <Link
            to="/partner"
            className="shrink-0 inline-flex items-center gap-1.5 text-rose-400 hover:text-rose-300 text-sm font-bold transition-colors whitespace-nowrap"
          >
            Apply to be featured
            <i className="ri-arrow-right-line text-base"></i>
          </Link>
        </div>
      </div>

      <section className="py-14 bg-gradient-to-r from-pink-500 to-rose-500">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl font-bold text-white mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>
            Spots fill fast — submit your event now!
          </h2>
          <p className="text-white/80 text-sm mb-6 max-w-xl mx-auto">
            Help other moms discover family-friendly happenings in Gwinnett County.
          </p>
          <button
            onClick={() => setShowSubmitModal(true)}
            className="inline-flex items-center gap-2 px-8 py-3 bg-white text-pink-600 text-sm font-bold rounded-full hover:bg-pink-50 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-add-circle-line text-base"></i>
            Submit an Event
          </button>
        </div>
      </section>

      <KeepExploring
        title="Keep Exploring"
        subtitle="Trusted by local moms across Gwinnett and surrounding areas."
        items={[
          { label: 'Explore Things To Do', icon: 'ri-compass-3-line', to: '/things-to-do' },
          { label: 'Seasonal Guides', icon: 'ri-sun-line', href: 'https://gwinnettcampguide.com' },
          { label: 'Join The Mommy Mingle', icon: 'ri-group-line', href: 'https://www.facebook.com/groups/themommymingle', primary: true },
        ]}
        bg="white"
      />

      <Footer />

      {selectedEvent && (
        <EventDetailModal
          event={selectedEvent}
          onClose={() => { setSelectedEvent(null); setIsGuestView(false); }}
          onRsvp={handleModalRsvp}
          isGuest={isGuestView}
          onUnlock={handleGuestUnlock}
        />
      )}

      {rsvpEvent && user && (
        <RsvpModal
          event={rsvpEvent}
          user={user}
          onClose={() => setRsvpEvent(null)}
        />
      )}

      {showSubmitModal && (
        <SubmitEventModal onClose={() => setShowSubmitModal(false)} />
      )}

      {showAuthGate && (
        <AccountGateModal
          onClose={() => { setShowAuthGate(false); setPendingRsvpEvent(null); }}
          onSuccess={handleAuthSuccess}
          onContinueAsGuest={handleContinueAsGuest}
        />
      )}
    </div>
  );
}
